/**
 * Declutter Manager
 *
 * Prevents visual overlap of direction icons on map route polylines using
 * spatial indexing for efficient collision detection.
 *
 * **Problem Solved:**
 * When displaying vehicle routes on a map, direction arrows are placed at regular
 * intervals. Without decluttering, these arrows overlap on curves, creating
 * visual chaos. This manager prevents overlaps using a QuadTree spatial index.
 *
 * **Key Features:**
 * - O(log n) overlap detection using QuadTree
 * - Configurable overlap threshold (allow partial overlaps)
 * - Dynamic boundary expansion
 * - "Last-in-wins" conflict resolution
 * - Per-frame reset for dynamic maps
 *
 * @module declutterManager
 *
 * @example
 * ```typescript
 * // Create manager instance
 * const manager = new DeclutterManager({
 *   threshold: 0,        // No overlap allowed
 *   includeTouch: false, // Touching edges don't count
 *   boundaryMargin: 64   // Extra boundary padding
 * })
 *
 * // During map rendering
 * manager.clear() // Reset for new frame
 *
 * routes.forEach(route => {
 *   route.iconPositions.forEach(pos => {
 *     const bbox = { x: pos.x - 19, y: pos.y - 19, w: 38, h: 38 }
 *     const { overlap } = manager.isOverlapping(bbox)
 *
 *     if (!overlap) {
 *       renderIcon(pos)
 *       manager.add(bbox) // Track placement
 *     }
 *   })
 * })
 * ```
 *
 * @example
 * ```
 * Lifecycle in map rendering:
 *
 * Frame N:                      Frame N+1:
 * 1. clear()                    1. clear()
 * 2. Check icon A → add         2. Check icon A → add
 * 3. Check icon B → overlap!    3. Map panned, different icons
 * 4. Check icon C → add         4. Check icon D → add
 * 5. Render A, C                5. Render A, D
 * ```
 */

import {
  inflate,
  intersectionArea,
  makeBBoxFromAnchor,
  rectContains,
  rectIntersects,
  union,
} from './bboxUtils'
import { QuadTree } from './quadtree'
import type {
  AnchorOptions,
  BBox,
  DeclutterManagerOptions,
  OverlapOptions,
  QuadTreeOptions,
  RenderedFeature,
} from './types'

/**
 * Manages spatial collision detection for map icons
 *
 * Tracks successfully placed features and prevents new features from overlapping.
 * Uses a QuadTree for efficient spatial queries.
 */
export class DeclutterManager {
  /** Array of successfully rendered features */
  private rendered: RenderedFeature[] = []

  /** QuadTree spatial index (null until first feature added) */
  private qt: QuadTree | null = null

  /** Whether touching edges count as overlap */
  private includeTouch: boolean

  /** QuadTree configuration */
  private qtOpts: QuadTreeOptions

  /** Extra padding around QuadTree boundary */
  private boundaryMargin: number

  /** Minimum overlap percentage to trigger declutter */
  private threshold: number

  /**
   * Creates a new DeclutterManager instance
   *
   * @param opts - Configuration options
   * @param opts.includeTouch - Whether touching edges count as overlap (default: false)
   * @param opts.quadtree - QuadTree options (default: {capacity: 8, maxDepth: 8})
   * @param opts.boundaryMargin - Extra boundary padding in pixels (default: 64)
   * @param opts.boundary - Initial boundary (auto-calculated if omitted)
   * @param opts.threshold - Minimum overlap percentage 0-1 (default: 0)
   *
   * @example
   * ```typescript
   * // Strict decluttering
   * const strict = new DeclutterManager({
   *   threshold: 0,
   *   includeTouch: true
   * })
   *
   * // Lenient decluttering (allow 20% overlap)
   * const lenient = new DeclutterManager({
   *   threshold: 0.2,
   *   includeTouch: false
   * })
   * ```
   */
  constructor(opts: DeclutterManagerOptions = {}) {
    this.includeTouch = opts.includeTouch ?? false
    this.qtOpts = opts.quadtree ?? { capacity: 8, maxDepth: 8 }
    this.boundaryMargin = Math.max(0, opts.boundaryMargin ?? 64)
    this.threshold = Math.max(0, opts.threshold ?? 0)

    if (opts.boundary) {
      this.qt = new QuadTree(inflate(opts.boundary, this.boundaryMargin), this.qtOpts)
    }
  }

  /**
   * Returns all successfully rendered features
   *
   * @returns Read-only array of rendered features
   *
   * @example
   * ```typescript
   * const features = manager.getAll()
   * console.log(`Rendered ${features.length} icons`)
   * ```
   */
  getAll(): readonly RenderedFeature[] {
    return this.rendered
  }

  /**
   * Returns the number of rendered features
   *
   * @returns Count of features
   *
   * @example
   * ```typescript
   * console.log(`Currently tracking ${manager.size()} icons`)
   * ```
   */
  size(): number {
    return this.rendered.length
  }

  /**
   * Clears all rendered features and resets the spatial index
   *
   * **IMPORTANT:** Call this at the start of each rendering frame to reset
   * state. Failure to clear will cause features to accumulate across frames.
   *
   * @example
   * ```typescript
   * // Typical usage in map rendering loop
   * function onPostRender() {
   *   manager.clear()           // Reset for new frame
   *   directionManager.clear()
   *
   *   // Collect and render icons
   *   collectAllIcons()
   *
   *   directionManager.draw()
   * }
   * ```
   */
  clear(): void {
    this.rendered = []
    this.qt = null
  }

  /**
   * Adds a feature to the manager with conflict resolution
   *
   * Implements "last-in-wins" strategy: if the new feature overlaps existing
   * features, the existing features are removed and the new one is added.
   *
   * **Algorithm:**
   * 1. Ensure QuadTree boundary can fit the new feature
   * 2. Query QuadTree for potentially overlapping features
   * 3. If overlaps found, remove them and rebuild QuadTree
   * 4. Add new feature to array and QuadTree
   *
   * @param bbox - Bounding box of the feature to add
   * @param id - Optional unique identifier
   * @param data - Optional custom data
   *
   * @returns The added feature
   *
   * @example
   * ```typescript
   * // Add direction icon
   * const feature = manager.add(
   *   { x: 100, y: 200, w: 38, h: 38 },
   *   'route-123-icon-5',
   *   { routeId: 'route-123', color: 'red' }
   * )
   * ```
   *
   * @example
   * ```
   * Conflict resolution:
   *
   * Before add:           After add:
   * ┌────────┐            ┌────────┐
   * │   A    │            │   C    │  Feature C added
   * │  ┌─────┼────┐       │  ┌─────┼────┐
   * └──┼─────┘    │       └──┼─────┘    │  Features A & B removed
   *    │    B     │          │          │  (overlapped with C)
   *    └──────────┘          └──────────┘
   * ```
   */
  add(bbox: BBox, id?: string | number, data?: unknown): RenderedFeature {
    // init/expand boundary first (so we can query reliably)
    this.ensureIndexCanFit(bbox)

    if (this.qt) {
      const overlaps = this.qt
        .query(bbox)
        .filter(rf => rectIntersects(bbox, rf.bbox, this.includeTouch))

      if (overlaps.length > 0) {
        // remove overlaps from the array (by object reference)
        const set = new Set(overlaps)
        this.rendered = this.rendered.filter(rf => !set.has(rf))

        // rebuild quadtree from remaining items
        this.rebuildIndex(this.rendered, union(this.qt.boundary(), bbox))
      }
    }

    const feat: RenderedFeature = { id, bbox, data }
    this.rendered.push(feat)
    this.qt!.insert(feat)

    return feat
  }

  /**
   * Adds a feature using anchor point positioning
   *
   * Convenience method that creates a bbox from a center/topleft point
   * and size, then adds it to the manager.
   *
   * @param x - X coordinate of anchor point
   * @param y - Y coordinate of anchor point
   * @param w - Width of feature
   * @param h - Height of feature
   * @param id - Optional unique identifier
   * @param data - Optional custom data
   * @param anchorOpts - Anchor positioning options
   *
   * @returns The added feature
   *
   * @example
   * ```typescript
   * // Add icon centered at (500, 300)
   * manager.addAtAnchor(
   *   500, 300,      // Center position
   *   38, 38,        // Icon size
   *   'icon-1',
   *   { color: 'blue' },
   *   { anchor: 'center', padding: 2 }
   * )
   * ```
   */
  addAtAnchor(
    x: number,
    y: number,
    w: number,
    h: number,
    id?: string | number,
    data?: unknown,
    anchorOpts?: AnchorOptions,
  ): RenderedFeature {
    const bbox = makeBBoxFromAnchor(x, y, w, h, anchorOpts)
    return this.add(bbox, id, data)
  }

  /**
   * Checks if a candidate feature overlaps with existing features
   *
   * Uses QuadTree spatial filtering to reduce candidates, then performs
   * precise overlap detection with threshold-based rejection.
   *
   * **Algorithm:**
   * 1. Query QuadTree for nearby features (spatial filtering)
   * 2. For each nearby feature:
   *    - Check if bboxes intersect
   *    - Calculate overlap area
   *    - Calculate overlap percentage relative to smaller bbox
   *    - If percentage ≥ threshold, return overlap
   * 3. Return no overlap if all checks pass
   *
   * @param candidate - Bounding box to check for overlap
   * @param opts - Overlap detection options
   * @param opts.includeTouch - Whether touching edges count (overrides instance setting)
   * @param opts.ignoreId - Feature ID to ignore (useful for updates)
   *
   * @returns Object with overlap status, overlapping feature, and area
   *
   * @example
   * ```typescript
   * const candidateBBox = { x: 100, y: 100, w: 38, h: 38 }
   * const result = manager.isOverlapping(candidateBBox)
   *
   * if (result.overlap) {
   *   console.log(`Overlaps with feature ${result.with?.id}`)
   *   console.log(`Overlap area: ${result.area}px²`)
   * } else {
   *   // Safe to place icon
   *   manager.add(candidateBBox)
   * }
   * ```
   *
   * @example
   * ```
   * Overlap detection with threshold:
   *
   * threshold = 0.0:           threshold = 0.3:
   *
   * ┌────┐                    ┌────┐
   * │ A  │ ✗ ANY overlap      │ A  │ ✓ 15% overlap OK
   * └─┬──┘                    └─┬──┘
   *   │┌────┐                   │┌────┐
   *   └┤ B  │                   └┤ B  │
   *    └────┘                    └────┘
   *
   * ┌────┐                    ┌────┐
   * │ A ┌┼───┐                │ A ┌┼───┐
   * └───┼┘ B │                └───┼┘ B │
   *     └────┘                    └────┘
   * ✗ Overlaps                ✗ 50% overlap rejected
   * ```
   */
  isOverlapping(
    candidate: BBox,
    opts: OverlapOptions = {},
  ): {
    overlap: boolean
    with?: RenderedFeature
    area?: number
  } {
    if (!this.qt) return { overlap: false }

    const includeTouch = opts.includeTouch ?? this.includeTouch
    const nearby = this.qt.query(candidate)

    for (const rf of nearby) {
      if (opts.ignoreId != null && rf.id === opts.ignoreId) continue
      if (rectIntersects(candidate, rf.bbox, includeTouch)) {
        const overlapArea = intersectionArea(candidate, rf.bbox)

        // Calculate overlap percentage relative to the smaller area
        const candidateArea = candidate.w * candidate.h
        const rfArea = rf.bbox.w * rf.bbox.h
        const smallerArea = Math.min(candidateArea, rfArea)
        const overlapPercentage = smallerArea > 0 ? overlapArea / smallerArea : 0

        // Activate declutter only if overlap exceeds the threshold
        if (overlapPercentage >= this.threshold) {
          return { overlap: true, with: rf, area: overlapArea }
        }
      }
    }

    return { overlap: false }
  }

  /**
   * Checks overlap using anchor point positioning
   *
   * Convenience method that creates a bbox from anchor point and checks overlap.
   * Also returns the computed candidate bbox for inspection.
   *
   * @param x - X coordinate of anchor point
   * @param y - Y coordinate of anchor point
   * @param w - Width of feature
   * @param h - Height of feature
   * @param anchorOpts - Anchor positioning options
   * @param overlapOpts - Overlap detection options
   *
   * @returns Overlap result including the computed candidate bbox
   *
   * @example
   * ```typescript
   * // Check if icon at (500, 300) would overlap
   * const result = manager.isOverlappingAtAnchor(
   *   500, 300,
   *   38, 38,
   *   { anchor: 'center' }
   * )
   *
   * if (!result.overlap) {
   *   renderIcon(500, 300)
   *   manager.add(result.candidate)
   * }
   * ```
   */
  isOverlappingAtAnchor(
    x: number,
    y: number,
    w: number,
    h: number,
    anchorOpts?: AnchorOptions,
    overlapOpts?: OverlapOptions,
  ): { overlap: boolean; with?: RenderedFeature; area?: number; candidate: BBox } {
    const candidate = makeBBoxFromAnchor(x, y, w, h, anchorOpts)
    const res = this.isOverlapping(candidate, overlapOpts)
    return { ...res, candidate }
  }

  /**
   * Returns the current QuadTree boundary
   *
   * @returns Current boundary, or null if no QuadTree initialized
   *
   * @example
   * ```typescript
   * const boundary = manager.getBoundary()
   * if (boundary) {
   *   console.log(`Boundary: ${boundary.w}×${boundary.h}`)
   * }
   * ```
   */
  getBoundary(): BBox | null {
    return this.qt ? this.qt.boundary() : null
  }

  /* ----------- private ----------- */

  /**
   * Ensures the QuadTree boundary can contain the candidate feature
   *
   * Performs lazy initialization on first feature, and dynamically expands
   * the boundary when features fall outside the current bounds.
   *
   * **Algorithm:**
   * 1. If no QuadTree exists, create one around the candidate (lazy init)
   * 2. If candidate fits in current boundary, do nothing
   * 3. Otherwise, compute union of current boundary + candidate
   * 4. Rebuild QuadTree with expanded boundary
   *
   * @param candidate - Feature that must fit in the boundary
   *
   * @internal
   *
   * @example
   * ```
   * Boundary expansion:
   *
   * Initial state:          Feature outside:        After expansion:
   * ┌──────────┐            ┌──────────┐            ┌───────────────────┐
   * │  Tree    │            │  Tree    │            │   Expanded Tree   │
   * │  ●  ●    │            │  ●  ●    │            │   ●  ●            │
   * │    ●  ●  │            │    ●  ●  │  + ●  →    │     ●  ●       ●  │
   * │  ●       │            │  ●       │            │   ●               │
   * └──────────┘            └──────────┘            │                   │
   *                                                 └───────────────────┘
   *                                                 (Rebuild entire tree)
   * ```
   */
  private ensureIndexCanFit(candidate: BBox): void {
    if (!this.qt) {
      // Lazy initialization: create QuadTree on first feature
      this.qt = new QuadTree(inflate(candidate, this.boundaryMargin), this.qtOpts)
      return
    }

    const current = this.qt.boundary()
    if (rectContains(current, candidate)) return

    // Candidate outside boundary: expand and rebuild
    const newBoundary = inflate(union(current, candidate), this.boundaryMargin)
    this.rebuildIndex(this.rendered, newBoundary)
  }

  /**
   * Rebuilds the QuadTree with a new boundary
   *
   * Creates a fresh QuadTree and reinserts all features.
   * Called when boundary needs to be expanded or after removing overlapping features.
   *
   * @param items - Features to insert into new tree
   * @param boundary - New boundary for the tree
   *
   * @internal
   *
   * @example
   * ```
   * Rebuild process:
   *
   * Old tree:               New tree:               After reinsert:
   * ┌─────┬─────┐          ┌─────────────────┐     ┌─────────────────┐
   * │ ● ● │     │          │                 │     │ ● ●             │
   * ├─────┼─────┤   →      │   Empty         │  →  │       ●   ●     │
   * │     │ ● ● │          │                 │     │                 │
   * └─────┴─────┘          └─────────────────┘     └─────────────────┘
   *                        Larger boundary         All features reinserted
   * ```
   */
  private rebuildIndex(items: RenderedFeature[], boundary: BBox): void {
    this.qt = new QuadTree(inflate(boundary, this.boundaryMargin), this.qtOpts)
    for (const it of items) this.qt.insert(it)
  }
}
